export const loadCloudAccessCatalog = async <T>(load: (provider: string) => Promise<T[]>) => {
  const providers = ['aws', 'azure'];
  const results = await Promise.allSettled(providers.map((provider) => load(provider)));
  const items: T[] = [];
  const failures: Array<{ provider: string; error: unknown }> = [];
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') items.push(...result.value);
    else failures.push({ provider: providers[index], error: result.reason });
  });
  return { items, failures };
};

const cloudAccessVerificationAttempts = 61;
const cloudAccessVerificationIntervalMs = 15_000;

const waitForRetry = (milliseconds: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    signal?.throwIfAborted();
    const cancel = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', cancel);
      resolve();
    }, milliseconds);
    signal?.addEventListener('abort', cancel, { once: true });
  });

type VerificationProblem = { status: number; code?: string };

const verificationProblem = async (error: unknown): Promise<VerificationProblem | null> => {
  const response = (error as { response?: Response } | null)?.response;
  if (!(response instanceof Response)) return null;
  try {
    // Keep the original response body readable by the normal error display.
    const { code } = await response.clone().json();
    return { status: response.status, code };
  } catch {
    return null;
  }
};

const isRetryableVerificationProblem = (problem: VerificationProblem | null): boolean => {
  if (!problem) return false;
  return (
    (problem.status === 409 && problem.code === 'azure_access_pending') ||
    (problem.status === 429 && problem.code === 'verification_busy') ||
    (problem.status === 503 && problem.code === 'azure_rate_limited') ||
    (problem.status === 504 && problem.code === 'verification_timeout')
  );
};

export const pollCloudAccessVerification = async <T>(
  verify: (signal?: AbortSignal) => Promise<T>,
  options: {
    signal?: AbortSignal;
    attempts?: number;
    intervalMs?: number;
    wait?: (milliseconds: number, signal?: AbortSignal) => Promise<void>;
    onAzureAccessPending?: () => Promise<void> | void;
  } = {}
): Promise<T> => {
  const attempts = Math.max(1, options.attempts ?? cloudAccessVerificationAttempts);
  const intervalMs = Math.max(0, options.intervalMs ?? cloudAccessVerificationIntervalMs);
  const wait = options.wait ?? waitForRetry;
  let pendingNotified = false;
  for (let attempt = 0; ; attempt += 1) {
    options.signal?.throwIfAborted();
    try {
      const result = await verify(options.signal);
      options.signal?.throwIfAborted();
      return result;
    } catch (error) {
      options.signal?.throwIfAborted();
      const problem = await verificationProblem(error);
      if (attempt + 1 >= attempts || !isRetryableVerificationProblem(problem)) throw error;
      if (!pendingNotified && problem?.status === 409 && problem.code === 'azure_access_pending') {
        pendingNotified = true;
        await options.onAzureAccessPending?.();
      }
      await wait(intervalMs, options.signal);
    }
  }
};
